// Global (not per-user) on/off switches backed by the `app_flags` table.
// A key with no row uses its coded default. Per-user betas keep using
// `beta_features` / `user_beta_access` (server/lib/beta.ts).

export const APP_FLAGS = {
  /** In-repo daily blog post automation (B15). Off until Travis flips it. */
  blogAutomationEnabled: {
    default: false,
    description: "Daily blog post automation from content/blog-topics.json",
  },
  /** Inbound Outrank article webhook. Outrank is discontinued (Oct 3, 2026). */
  outrankWebhookEnabled: {
    default: false,
    description: "Accept articles pushed by Outrank to /api/webhook/outrank",
  },
  /**
   * Daily Pulse jobs, emails, screens and navigation (B18). Off by default:
   * the pulse crons skip, /api/app/pulse/* answers 404, /app/daily-pulse
   * redirects to the dashboard and the pinned nav item is gone.
   */
  dailyPulseEnabled: {
    default: false,
    description: "Daily Pulse briefing, digest emails and navigation",
  },
  /** B21 global kill switch for daily buy box agents (scheduled runs + admin Run now). */
  buyBoxAutoEnabled: {
    default: true,
    description: "Daily buy box agents: scheduled ~6am runs and match emails",
  },
  homepageV2Enabled: {
    default: false,
    description: "Serve Homepage v2 at / (marketing site). Off = v1.",
  },
} as const satisfies Record<string, { default: boolean; description: string }>;

export type AppFlagKey = keyof typeof APP_FLAGS;

export const APP_FLAG_KEYS = Object.keys(APP_FLAGS) as AppFlagKey[];

/**
 * Flags the signed-in app may read through GET /api/app/flags
 * (server/routes/app-flags-public.ts). Only non-sensitive UI switches belong here.
 */
export const CLIENT_VISIBLE_APP_FLAG_KEYS = ["dailyPulseEnabled"] as const satisfies readonly AppFlagKey[];

export type ClientVisibleAppFlagKey = (typeof CLIENT_VISIBLE_APP_FLAG_KEYS)[number];

export type ClientAppFlags = Record<ClientVisibleAppFlagKey, boolean>;

/** Coded defaults for the client-visible flags (used until the endpoint answers). */
export function defaultClientAppFlags(): ClientAppFlags {
  return Object.fromEntries(
    CLIENT_VISIBLE_APP_FLAG_KEYS.map((k) => [k, APP_FLAGS[k].default]),
  ) as ClientAppFlags;
}

export function isAppFlagKey(key: string): key is AppFlagKey {
  return Object.prototype.hasOwnProperty.call(APP_FLAGS, key);
}
